<template>
  <div class="salary-settlement-list">
    <sme-alert v-if="dataTruncated" level="warning" class="mb-3">
      Data has been truncated, so totals may not be accurate. Please narrow your search criteria.
    </sme-alert>
    <page-list-header>
      <template #left>
        <filter-dropdown v-model="filters" :filters="filterItems">
          <inline-search v-if="!employeeId" v-model="searchText" @search="onSearch" />
        </filter-dropdown>
      </template>
      <template #right>
        <b-form-group label-cols="auto" label="Per page" class="mb-0">
          <b-form-select v-model="perPage" :options="SALARY_SETTLEMENT_PER_PAGE_OPTIONS" />
        </b-form-group>
        <export-button
          :export-data="filteredSettlements"
          :export-file-rows="exportFileRows"
          :export-headers="exportHeaders"
          file-name="salary-settlement"
        />
      </template>
    </page-list-header>
    <component :is="componentType" class="mb-3">
      <!-- <app-loading :loading="loading" /> -->

      <b-table
        v-if="!loading"
        :items="settlements"
        :fields="fieldDefinitions"
        filter="''"
        :filter-function="filterFunction"
        :current-page="currentPage"
        :per-page="perPage"
        class="mb-0"
        table-class="table-minimal"
        :tfoot-class="!settlements?.length ? 'd-none' : ''"
        primary-key="salary_id"
        foot-clone
        no-footer-sorting
        responsive
        show-empty
        @filtered="update"
      >
        <template v-slot:cell(full_name)="data">
          <router-link :to="{ name: 'employee', params: { employeeId: data?.item?.employee_id } }">
            {{ data?.item?.full_name }}
          </router-link>
        </template>
        <template v-slot:foot()>&nbsp;</template>
        <template v-slot:foot(salary_amount)>{{ totalSalaryAmount }}</template>
        <template v-slot:foot(amount_settled)>{{ totalAmountSettled }}</template>
        <template v-slot:foot(total_transferred)>{{ totalTransferred }}</template>
      </b-table>
    </component>

    <b-pagination
      v-model="currentPage"
      :total-rows="filteredSettlements.length"
      :per-page="perPage"
      class="justify-content-end"
    />
  </div>
</template>

<script setup>
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';
import pick from 'lodash/pick';
import sumBy from 'lodash/sumBy';
import moment from 'moment';
import { computed, onBeforeMount, ref, watch } from 'vue';
import ApiClient from '@/ApiClient';
import { CONTROL_TYPES } from '@/components/AppInput.vue';
import ExportButton from '@/components/ExportButton.vue';
import FilterDropdown from '@/components/FilterDropdown.vue';
import InlineSearch from '@/components/InlineSearch.vue';
import PageListHeader from '@/components/PageListHeader.vue';
import SmeAlert from '@/components/atoms/SmeAlert.vue';
import SmeCard from '@/components/atoms/SmeCard.vue';
import {
  SALARY_SETTLEMENT_FILTER_KEYS,
  SALARY_SETTLEMENT_SEARCH_FIELDS,
  SALARY_SETTLEMENT_API_FILTER_KEYS,
  SALARY_SETTLEMENT_FIELD_DEFINITIONS,
  SALARY_SETTLEMENT_FIELD_KEYS,
  SALARY_SETTLEMENT_PER_PAGE_OPTIONS,
} from '@/pages/salary-settlement/constants';
import { toCurrency } from '@/utils';
import debounceAsync from '@/utils/debounceAsync';

const STARTS_ON_DEFAULT_VALUE = moment().startOf('month').format('YYYY-MM-DD');
const ENDS_ON_DEFAULT_VALUE = moment().format('YYYY-MM-DD');

const props = defineProps({
  nested: {
    type: Boolean,
    default: false,
  },
  employeeId: {
    type: String,
  },
  filterKeys: {
    type: Array,
    default: () => Object.values(SALARY_SETTLEMENT_FILTER_KEYS),
  },
  fieldKeys: {
    type: Array,
  },
});

const settlements = ref([]);
const filteredSettlements = ref([]);
const divisions = ref([]);
const employeeFilters = ref(null);
const loading = ref(true);
const initialized = ref(false);
const filters = ref({
  [SALARY_SETTLEMENT_FILTER_KEYS.STARTS_ON]: STARTS_ON_DEFAULT_VALUE,
  [SALARY_SETTLEMENT_FILTER_KEYS.ENDS_ON]: ENDS_ON_DEFAULT_VALUE,
});
const searchCriteria = ref('');
const searchText = ref('');
const currentPage = ref(1);
const perPage = ref(SALARY_SETTLEMENT_PER_PAGE_OPTIONS[0]);

const dataTruncated = computed(() => filteredSettlements.value.length >= 10000);

const fieldDefinitions = computed(() => {
  return SALARY_SETTLEMENT_FIELD_DEFINITIONS.filter(field =>
    (props.fieldKeys || Object.values(SALARY_SETTLEMENT_FIELD_KEYS)).includes(field.key),
  );
});

const exportHeaders = computed(() => fieldDefinitions.value.map(field => field.label));

const exportFileRows = settlement => {
  return fieldDefinitions.value.map(field => {
    const value = settlement[field.key];
    return field.formatter ? field.formatter(value) : value;
  });
};

const filterItems = computed(() =>
  [
    {
      key: SALARY_SETTLEMENT_FILTER_KEYS.STARTS_ON,
      label: 'From',
      type: CONTROL_TYPES.DATE,
      defaultValue: STARTS_ON_DEFAULT_VALUE,
      controlProps: {
        max: filters.value[SALARY_SETTLEMENT_FILTER_KEYS.ENDS_ON]
          ? filters.value[SALARY_SETTLEMENT_FILTER_KEYS.ENDS_ON]
          : null,
      },
    },
    {
      key: SALARY_SETTLEMENT_FILTER_KEYS.ENDS_ON,
      label: 'To',
      type: CONTROL_TYPES.DATE,
      defaultValue: ENDS_ON_DEFAULT_VALUE,
      controlProps: {
        min: filters.value[SALARY_SETTLEMENT_FILTER_KEYS.STARTS_ON]
          ? filters.value[SALARY_SETTLEMENT_FILTER_KEYS.STARTS_ON]
          : null,
      },
    },
    {
      key: SALARY_SETTLEMENT_FILTER_KEYS.DIVISION,
      label: 'Division',
      type: CONTROL_TYPES.CHECKBOX,
      options: divisions.value.map(division => ({
        text: division.name,
        value: division.division_id,
      })),
      hidden: !divisions.value.length,
    },
  ].filter(filter => props.filterKeys.includes(filter.key)),
);

const componentType = computed(() => (props.nested ? 'div' : SmeCard));

const appliedApiFilters = computed(() =>
  omitBy(pick(filters.value, SALARY_SETTLEMENT_API_FILTER_KEYS), value => isNil(value)),
);

const filterConditions = computed(() => {
  const conditions = [];

  if (searchCriteria.value) {
    conditions.push(settlement => {
      return Object.values(pick(settlement, SALARY_SETTLEMENT_SEARCH_FIELDS)).some(value =>
        value?.toString().toLowerCase().startsWith(searchCriteria.value.toLowerCase()),
      );
    });
  }

  if (filters.value[SALARY_SETTLEMENT_FILTER_KEYS.DIVISION]?.length) {
    conditions.push(settlement => {
      return filters.value[SALARY_SETTLEMENT_FILTER_KEYS.DIVISION].some(divisionFilter => {
        const divisionName = divisions.value.find(division => division.division_id === divisionFilter)?.name;

        return employeeFilters.value.division_name?.[divisionName]?.includes(settlement?.employee_id) || false;
      });
    });
  }

  return conditions;
});

const totalSalaryAmount = computed(() =>
  toCurrency(sumBy(filteredSettlements.value, settlement => settlement.salary_amount || 0)),
);
const totalAmountSettled = computed(() =>
  toCurrency(sumBy(filteredSettlements.value, settlement => settlement.amount_settled || 0)),
);
const totalTransferred = computed(() =>
  toCurrency(sumBy(filteredSettlements.value, settlement => settlement.total_transferred || 0)),
);

watch(settlements, settlements => update(settlements.filter(filterFunction)));

watch(searchText, () => {
  update(settlements.value.filter(filterFunction));
});

const getSalarySettlements = debounceAsync(async () => {
  const params = props.employeeId
    ? { ...appliedApiFilters.value, employee_id: props.employeeId }
    : appliedApiFilters.value;
  const response = await ApiClient.getSalarySettlement(params);
  loading.value = false;
  const rawSettlements = response?.data || [];
  settlements.value = rawSettlements.sort((a, b) => new Date(b.salary_completed_at) - new Date(a.salary_completed_at));
}, 200);

const getDivisions = async () => (divisions.value = (await ApiClient.getDivisions())?.data || []);

const getEmployeeFilters = async () =>
  (employeeFilters.value = (await ApiClient.getFilters())?.filters_employees || {});

const update = settlements => {
  filteredSettlements.value = settlements;
  currentPage.value = 1;
};

const filterFunction = item =>
  filterConditions.value.length === 0 || filterConditions.value.every(condition => condition(item));

const onSearch = searchValue => {
  searchCriteria.value = searchValue;
  update(settlements.value.filter(filterFunction));
};

onBeforeMount(async () => {
  watch(
    appliedApiFilters,
    async (newFilters, prevFilters) => {
      loading.value = true;
      if (!initialized.value) {
        await Promise.all([getDivisions(), getEmployeeFilters(), getSalarySettlements()]);
      } else {
        if (!isEqual(newFilters, prevFilters)) {
          await getSalarySettlements();
        }
      }

      loading.value = false;
      initialized.value = true;
    },
    {
      deep: true,
      immediate: true,
    },
  );
});
</script>

<style lang="scss" scoped>
.salary-settlement-list {
  .table-minimal {
    th,
    td {
      border: none;
      padding: 0.5rem 0.75rem;
    }

    thead th {
      border-bottom: 2px solid var(--palette-color-default-lighten-80);
      font-weight: 600;
    }

    tbody tr:hover {
      background-color: rgba(0, 0, 0, 0.075);
    }

    tfoot td {
      border-top: 2px solid var(--palette-color-default-lighten-80);
      font-weight: 600;
    }
  }
}
</style>
